import { useState, useEffect, useCallback } from 'react';
import Papa from 'papaparse';

export interface Product {
  id: string | number;
  name: string;
  cat: string;
  tag?: string;
  img: string;
}

const SHEET_ID = '1HPWS44lLyC3UwWNNnblA178ZjS1wSDa0Savr9zNk-CQ';
const SHEET_CSV_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:csv`;
const SHEET_EXPORT_CSV_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/export?format=csv`;

/** CORS proxy so browser can fetch Google Sheets CSV (Google often blocks direct XHR). */
const CORS_PROXY = 'https://api.allorigins.win/raw?url=';

const PRODUCT_IMAGE_BASE = 'https://manlab.co.zw';

/** Ensure product image URL is absolute and valid. (Exported for tests.) */
export function normalizeImageUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${PRODUCT_IMAGE_BASE}${path}`;
}

/** True if string looks like an image URL. */
function looksLikeUrl(s: string): boolean {
  return /^https?:\/\//i.test(s) || s.startsWith('/') || s.includes('manlab.co.zw') || s.includes('wp-content');
}

/** Normalize CSV header key (strip BOM, trim) for matching. */
function normKey(k: string): string {
  return k.replace(/^\uFEFF/, '').trim();
}

/** Get image URL from row – check known column names and any column whose value looks like a URL. (Exported for tests.) */
export function getImageFromRow(row: Record<string, unknown>): string {
  const keys = Object.keys(row);
  // Prefer columns that look like image fields (any casing; strip BOM)
  const imageLikeKeys = keys.filter(k => /image|img|photo|picture|url|src|media|attachment/i.test(normKey(k)) || /^Images?\.?\d*$/i.test(normKey(k)));
  const tryKeys = ['Images', 'Image', 'Product Image', 'Image 1', 'Product images', ...imageLikeKeys];
  for (const key of tryKeys) {
    const raw = row[key];
    if (raw == null || raw === '') continue;
    const imgStr = String(raw).replace(/['"]/g, '').trim();
    const first = imgStr.split(/[,\s]+/)[0].trim();
    if (!first) continue;
    if (looksLikeUrl(first)) return normalizeImageUrl(first);
    const url = normalizeImageUrl(first);
    if (url) return url;
  }
  // Fallback: any column containing a URL (e.g. manlab.co.zw, wp-content)
  for (const key of keys) {
    const raw = row[key];
    if (raw == null || raw === '') continue;
    const s = String(raw).replace(/['"]/g, '').trim();
    const first = s.split(/[,\s]+/)[0].trim();
    if (looksLikeUrl(first)) return normalizeImageUrl(first);
  }
  return '';
}

export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const processCsvText = (csvText: string) => {
    const results = Papa.parse(csvText, { header: true, skipEmptyLines: true });
    const data = results.data as any[];
    const parsedProducts: Product[] = [];

    if (process.env.NODE_ENV === 'development' && data.length > 0) {
      const firstRow = data[0] as Record<string, unknown>;
      const colNames = Object.keys(firstRow);
      console.log('[useProducts] CSV columns:', colNames.map(normKey));
      colNames.forEach(k => {
        const v = firstRow[k];
        if (v != null && String(v).includes('manlab.co.zw')) console.log('[useProducts] Column with manlab URL:', normKey(k), '=>', String(v).slice(0, 80) + (String(v).length > 80 ? '...' : ''));
      });
      const sampleImg = getImageFromRow(firstRow);
      console.log('[useProducts] First row image value:', sampleImg || '(none)');
    }

    data.forEach(row => {
      const imgUrl = getImageFromRow(row as Record<string, unknown>);
      if (row['Type'] !== 'variation' && row['Name'] && row['Published'] === '1') {
        const fullCat = row['Categories'] || 'Medical Supplies';
        const catParts = fullCat.split(/[>,]/);
        const firstCat = catParts[0].trim();
        let shortCat = 'supplies';
        const lowerCat = fullCat.toLowerCase();
        if (lowerCat.includes('electrosurgical')) shortCat = 'electrosurgical';
        else if (lowerCat.includes('forceps')) shortCat = 'forceps';
        else if (lowerCat.includes('lab') || lowerCat.includes('reagent')) shortCat = 'lab';
        else if (lowerCat.includes('ob/gyn') || lowerCat.includes('gynecol') || lowerCat.includes('vaginal')) shortCat = 'obgyn';
        else if (lowerCat.includes('leadcare') || lowerCat.includes('lead test')) shortCat = 'leadcare';
        parsedProducts.push({
          id: row['ID'] || Math.random().toString(36).substr(2, 9),
          name: row['Name'].trim(),
          cat: shortCat,
          tag: firstCat,
          img: imgUrl || ''
        });
      }
    });

    if (process.env.NODE_ENV === 'development' && parsedProducts.length > 0) {
      console.log('[useProducts] First product img:', parsedProducts[0].img || '(none)');
    }
    setProducts(parsedProducts);
    setLoading(false);
  };

  const tryParse = useCallback((url: string, viaProxy = false) => {
    const fetchUrl = viaProxy ? CORS_PROXY + encodeURIComponent(url) : url;

    fetch(fetchUrl)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(csvText => {
        try {
          processCsvText(csvText);
        } catch (err: any) {
          console.error('Error extracting products:', err);
          setError(err.message);
          setLoading(false);
        }
      })
      .catch((err: any) => {
        if (!viaProxy) {
          console.warn('[useProducts] Direct fetch failed (likely CORS), trying CORS proxy:', err.message);
          tryParse(url, true);
          return;
        }
        if (url === SHEET_CSV_URL) {
          console.warn('[useProducts] gviz failed, trying export URL');
          tryParse(SHEET_EXPORT_CSV_URL, true);
          return;
        }
        console.error('Error fetching CSV:', err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    tryParse(SHEET_CSV_URL);
  }, [tryParse]);

  return { products, loading, error };
};
